/**
 * API Request Cache and Deduplication Utility
 *
 * This cache prevents duplicate API requests from being made when multiple
 * dashboard widgets request the same data simultaneously.
 *
 * Example: If 3 widgets all call getProgramStatistics with the same parameters,
 * only 1 actual API request will be made, and all 3 widgets will receive the result.
 */

import { isDevelopment } from './environment.ts';

class ApiCache {
  constructor() {
    // Store for cached responses
    this.cache = new Map();

    // Store for in-flight requests (promises)
    this.pendingRequests = new Map();

    // Default cache TTL (5 minutes)
    this.defaultTTL = 5 * 60 * 1000;
  }

  /**
   * Generate a cache key from request parameters
   */
  generateKey(endpoint, params) {
    const sortedParams = params ? JSON.stringify(params, Object.keys(params).sort()) : '';
    return `${endpoint}::${sortedParams}`;
  }

  /**
   * Get cached data or fetch from API
   *
   * @param {string} endpoint - API endpoint identifier
   * @param {object} params - Request parameters
   * @param {function} fetchFn - Function that returns a promise to fetch the data
   * @param {number} ttl - Time to live for cache entry in milliseconds
   * @returns {Promise} Promise that resolves to the API response
   */
  async get(endpoint, params, fetchFn, ttl = this.defaultTTL) {
    if (isDevelopment()) {
      return fetchFn();
    }

    const key = this.generateKey(endpoint, params);

    // Check if we have a valid cached response
    const cached = this.cache.get(key);
    if (cached && Date.now() < cached.expiresAt) {
      return Promise.resolve(cached.data);
    }

    // Check if there's already a request in flight for this key
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key);
    }

    // Make new request
    const promise = fetchFn()
      .then(response => {
        // Cache the response
        this.cache.set(key, {
          data: response,
          expiresAt: Date.now() + ttl,
        });

        // Remove from pending requests
        this.pendingRequests.delete(key);

        return response;
      })
      .catch(error => {
        // Remove from pending requests on error
        this.pendingRequests.delete(key);
        throw error;
      });

    // Store the pending promise
    this.pendingRequests.set(key, promise);

    return promise;
  }

  /**
   * Invalidate cache entries by endpoint
   */
  invalidate(endpoint) {
    Array.from(this.cache.keys())
      .filter(key => key.startsWith(`${endpoint}::`))
      .forEach(key => {
      this.cache.delete(key);
      });
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  /**
   * Get cache statistics for debugging
   */
  getStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      entries: Array.from(this.cache.keys()),
    };
  }
}

// Export singleton instance
export const apiCache = new ApiCache();

/**
 * Wrapper function for caching Redux action dispatches
 *
 * Usage:
 * const result = await cachedDispatch(
 *   'program_statistics',
 *   { orgId: '123', startTimestamp: '2024-01-01' },
 *   () => dispatch(actions.getProgramStatistics(request))
 * );
 */
export const cachedDispatch = (endpoint, params, dispatchFn, ttl) => {
  return apiCache.get(endpoint, params, dispatchFn, ttl);
};

export default apiCache;
